import React, {useCallback, useMemo} from 'react';
import {CircularProgress} from '@mui/material';
import {Icon} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {
  CONTENT_SUBMISSION_ACTIONS_BY_STATE,
  INACTIVE_CAMPAIGN_CONTENT_SUBMISSION_ACTIONS,
  MIXED_MEDIA_CHILD_EXCLUDED_CONTENT_SUBMISSION_ACTIONS,
  MIXED_MEDIA_EXCLUDED_CONTENT_SUBMISSION_ACTIONS,
} from '@/config/contentSubmissionActions';
import {ACTIVE_CAMPAIGN_STATES} from '@/types/campaign';
import {
  ContentSubmission,
  ContentSubmissionAnalysisStatus,
  CONTENT_SUBMISSION_ANALYSIS_STATUS,
  CONTENT_SUBMISSION_KIND,
  CONTENT_SUBMISSION_STATUS,
} from '@/types/contentSubmission';
import refreshContentSubmissionIfProcessing from '@/views/campaigns/content-submissions/utils/refreshContentSubmission';
import {DropdownOption} from '@/components/dropdown';
import {
  CONTENT_SUBMISSION_ACTION,
  ContentSubmissionAction,
} from './contentSubmissionCardTypes';

export type ActionMenuItem = DropdownOption & {
  action: ContentSubmissionAction;
};

type ActionMenuItemDefinition = {
  id: string;
  action: ContentSubmissionAction;
  localeLabelKey: string;
  startIcon: React.ReactNode;
  disabled?: boolean;
  /**
   * When true, renders a visual group separator above this item in the
   * dropdown — used to split workflow actions (Approve / Request Edits /
   * Archive) from read-only actions (Share / Download).
   */
  separatorBefore?: boolean;
};

const AI_ICON = (
  <Icon name="Features-Effects" size="medium" appearance="neutral" />
);

const AI_LOADING_ICON = <CircularProgress size={18} />;

// Order matters here — items render in this order in the kebab menu.
// The list is split into two groups separated by a visual divider:
//
//   Group 1 — workflow actions that change the asset's state
//     Approve, Request Edits, Archive, Unarchive
//   ── divider ──
//   Group 2 — read-only / external actions
//     Share, Download
//
// AI analysis items (admin-only) get appended below Group 2 by the hook
// itself — they're conceptually part of Group 2 (read-only utility actions).
const BASE_ITEM_DEFS: ActionMenuItemDefinition[] = [
  {
    id: 'accept',
    action: CONTENT_SUBMISSION_ACTION.ACCEPT,
    localeLabelKey: 'Approve',
    startIcon: (
      <Icon
        name="Actions-Patch-Accept-Line"
        size="medium"
        appearance="neutral"
      />
    ),
  },
  {
    id: 'requestEdits',
    action: CONTENT_SUBMISSION_ACTION.REJECT,
    localeLabelKey: 'Request Edits',
    startIcon: (
      <Icon name="Navigation-Edit" size="medium" appearance="neutral" />
    ),
  },
  {
    id: 'archive',
    action: CONTENT_SUBMISSION_ACTION.ARCHIVE,
    localeLabelKey: 'Archive',
    startIcon: (
      <Icon name="Features-Reject" size="medium" appearance="neutral" />
    ),
  },
  {
    id: 'unarchive',
    action: CONTENT_SUBMISSION_ACTION.UNARCHIVE,
    localeLabelKey: 'Move to Awaiting Review',
    startIcon: <Icon name="StateBar-Undo" size="medium" appearance="neutral" />,
  },
  {
    id: 'share',
    action: CONTENT_SUBMISSION_ACTION.SHARE,
    localeLabelKey: 'Share',
    startIcon: <Icon name="Actions-Share" size="medium" appearance="neutral" />,
    // First item of Group 2 — separates workflow actions from read-only ones.
    separatorBefore: true,
  },
  {
    id: 'download',
    action: CONTENT_SUBMISSION_ACTION.DOWNLOAD,
    localeLabelKey: 'Download',
    startIcon: (
      <Icon name="Navigation-Download" size="medium" appearance="neutral" />
    ),
  },
];

const OFF_PLATFORM_ACTIONS: ContentSubmissionAction[] = [
  CONTENT_SUBMISSION_ACTION.SHARE,
  CONTENT_SUBMISSION_ACTION.ACCEPT,
];

function translateItem(def: ActionMenuItemDefinition): ActionMenuItem {
  return {
    id: def.id,
    action: def.action,
    label: translate(def.localeLabelKey),
    startIcon: def.startIcon,
    disabled: def.disabled,
    separatorBefore: def.separatorBefore,
  };
}

type UseContentSubmissionActionMenuItemsParams = {
  isAdmin?: boolean;
  analysisStatus?: ContentSubmissionAnalysisStatus | null;
  isOffPlatform?: boolean;
  kind?: string | null;
  aasmState?: string | null;
  archivedFromState?: string | null;
  campaignState?: string | null;
  roleAlreadyApproved?: boolean;
  parentContentSubmissionId?: string | null;
  downloadStatus?: {
    status: 'idle' | 'downloading' | 'done' | 'error';
    progress: number;
  };
  hasDownloadUrl?: boolean;
};

type UseContentSubmissionActionMenuItemsReturn = {
  items: ActionMenuItem[];
  onMenuClose: (contentSubmission: ContentSubmission) => void;
};

function useContentSubmissionActionMenuItems({
  isAdmin = false,
  analysisStatus,
  isOffPlatform = false,
  kind,
  aasmState,
  archivedFromState,
  campaignState,
  roleAlreadyApproved = false,
  parentContentSubmissionId,
  downloadStatus = {status: 'idle' as const, progress: 0},
  hasDownloadUrl = true,
}: UseContentSubmissionActionMenuItemsParams): UseContentSubmissionActionMenuItemsReturn {
  const onMenuClose = useCallback(
    async (contentSubmission: ContentSubmission) => {
      try {
        await refreshContentSubmissionIfProcessing(contentSubmission);
      } catch {
        // If the refresh fails, the user can manually refresh the page
      }
    },
    []
  );

  const items = useMemo(() => {
    const allowedActions = aasmState
      ? (CONTENT_SUBMISSION_ACTIONS_BY_STATE[aasmState] ?? [])
      : [];

    const isCampaignActive =
      !!campaignState && ACTIVE_CAMPAIGN_STATES.includes(campaignState);

    const isMixedMedia = kind === CONTENT_SUBMISSION_KIND.MIXED_MEDIA;
    const isMixedMediaOrChild = isMixedMedia || !!parentContentSubmissionId;

    // Each rule encodes one visibility constraint for the current render context.
    // Adding a new content type or context = append one rule; no need to touch the chain.
    const contextRules: Array<(action: ContentSubmissionAction) => boolean> = [
      (action) => allowedActions.includes(action),
      (action) => !isOffPlatform || OFF_PLATFORM_ACTIONS.includes(action),
      (action) =>
        action !== CONTENT_SUBMISSION_ACTION.ACCEPT || !roleAlreadyApproved,
      (action) =>
        isCampaignActive ||
        INACTIVE_CAMPAIGN_CONTENT_SUBMISSION_ACTIONS.includes(action),
      (action) =>
        !isMixedMedia ||
        !MIXED_MEDIA_EXCLUDED_CONTENT_SUBMISSION_ACTIONS.includes(action),
      (action) =>
        !isMixedMediaOrChild ||
        !MIXED_MEDIA_CHILD_EXCLUDED_CONTENT_SUBMISSION_ACTIONS.includes(action),
      (action) =>
        action !== CONTENT_SUBMISSION_ACTION.DOWNLOAD || hasDownloadUrl,
    ];

    const defs = BASE_ITEM_DEFS.filter((item) =>
      contextRules.every((rule) => rule(item.action))
    );

    const supportsAiAnalysis =
      aasmState === CONTENT_SUBMISSION_STATUS.PENDING ||
      aasmState === CONTENT_SUBMISSION_STATUS.REJECTED ||
      aasmState === CONTENT_SUBMISSION_STATUS.ARCHIVED;

    const isAnalysisCompleted =
      analysisStatus === CONTENT_SUBMISSION_ANALYSIS_STATUS.COMPLETED;

    if (isAdmin && !isOffPlatform && isAnalysisCompleted) {
      defs.push({
        id: 'viewAiAnalysis',
        action: CONTENT_SUBMISSION_ACTION.VIEW_AI_ANALYSIS,
        localeLabelKey: 'View AI analysis',
        startIcon: AI_ICON,
      });
    } else if (
      isAdmin &&
      !isOffPlatform &&
      isCampaignActive &&
      kind === 'video' &&
      supportsAiAnalysis
    ) {
      const isAnalysisProcessing =
        analysisStatus === CONTENT_SUBMISSION_ANALYSIS_STATUS.PROCESSING;

      if (isAnalysisProcessing) {
        defs.push({
          id: 'processingAiAnalysis',
          action: CONTENT_SUBMISSION_ACTION.VIEW_AI_ANALYSIS,
          localeLabelKey: 'Processing AI analysis',
          startIcon: AI_LOADING_ICON,
          disabled: true,
        });
      } else {
        defs.push({
          id: 'runAiAnalysis',
          action: CONTENT_SUBMISSION_ACTION.RUN_AI_ANALYSIS,
          localeLabelKey: 'Run AI analysis',
          startIcon: AI_ICON,
        });
      }
    }

    const defsWithDownloadState = defs.map((def) => {
      if (def.id !== 'download') {
        return def;
      }
      if (downloadStatus.status === 'downloading') {
        return {
          ...def,
          startIcon: (
            <CircularProgress
              size={16}
              variant="determinate"
              value={downloadStatus.progress}
              color="inherit"
            />
          ),
          localeLabelKey: 'Downloading',
          disabled: true,
        };
      }
      if (downloadStatus.status === 'done') {
        return {
          ...def,
          startIcon: (
            <Icon
              name="Actions-Patch-Accept-Line"
              size="medium"
              appearance="neutral"
            />
          ),
          localeLabelKey: 'Downloaded',
          disabled: true,
        };
      }
      if (downloadStatus.status === 'error') {
        return {
          ...def,
          startIcon: (
            <Icon name="Features-Reject" size="medium" appearance="neutral" />
          ),
          localeLabelKey: 'Failed',
          disabled: true,
        };
      }
      return def;
    });

    const withDynamicLabels = defsWithDownloadState.map((def) => {
      if (
        def.action === CONTENT_SUBMISSION_ACTION.UNARCHIVE &&
        archivedFromState === CONTENT_SUBMISSION_STATUS.REJECTED
      ) {
        return {...def, localeLabelKey: 'Move to Changes Requested'};
      }
      return def;
    });

    return withDynamicLabels.map(translateItem);
  }, [
    isAdmin,
    analysisStatus,
    isOffPlatform,
    kind,
    aasmState,
    archivedFromState,
    campaignState,
    roleAlreadyApproved,
    parentContentSubmissionId,
    downloadStatus,
    hasDownloadUrl,
  ]);

  return {items, onMenuClose};
}

export default useContentSubmissionActionMenuItems;
